import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, BehaviorSubject, switchMap, filter, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginRequest, JwtPayload, ResetPasswordRequest, ForgotPasswordRequest, ValidatePasswordResetTokenRequest, LoginResponse } from '../../data/auth.data';
import { UpdateUserRequest, User } from '../../data/user.data';
import { UserService } from './user.service';
import { jwtDecode } from 'jwt-decode';
import { LocalStorageService } from '../../utils/services/localstorage.service';
import { firstValueFrom } from 'rxjs';
@Injectable({
  providedIn: 'root',
})

export class AuthService {
  private userService = inject(UserService);
  private localStorageService = inject(LocalStorageService);
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/Auth`;

  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';

  private userSubject = new BehaviorSubject<User | null>(null);

  user$: Observable<User | null> = this.userSubject.asObservable();
  isLoaded = signal(false);

  ping(): Observable<string> {
    return this.http.get<string>(`${this.apiUrl}/ping`);
  }

  login(data: LoginRequest): Observable<User> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/login`, data).pipe(
      tap((response) => this.setTokens(response)),
      switchMap(() => {
        const userId = Number(this.getUserIdFromToken());
        return this.userService.getUser(userId);
      }),
      tap((user) => this.userSubject.next(user))
    );
  }

  getAccessToken(userId: number, refreshToken: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/get-access-token`,
      null,
      {
        params: {
          userId,
          refreshToken
        }
      });
  }

  forgotPassword(data: ForgotPasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/forgot-password`, data);
  }

  resetPassword(data: ResetPasswordRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/reset-password`, data);
  }

  validatePasswordResetToken(data: ValidatePasswordResetTokenRequest): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/validate-password-reset-token`, data);
  }

  getAccessTokenLocalStorage(): string | null {
    return this.localStorageService.getItem(this.ACCESS_TOKEN_KEY)
  }

  getRefreshTokenLocalStorage(): string | null {
    return this.localStorageService.getItem(this.REFRESH_TOKEN_KEY)
  }

  isTokenExpired(token: string): boolean {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (!decoded.exp) {
        return true;
      }
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime;
    } catch {
      return true
    }
  }

  get isAuthenticated(): boolean {
    return !!this.userSubject.value;
  }

  getUserIdFromToken(): string | null {
    const token = this.getAccessTokenLocalStorage();
    if (!token) {
      return null
    }

    try {
      const claims = jwtDecode<JwtPayload>(token);
      if (!claims)
        return null;

      return claims.sub;
    } catch {
      return null
    }
  }

  getUserId(): number | null {
    return this.userSubject.value?.id ?? null;
  }
  private setTokens(response: LoginResponse): void {
    this.localStorageService.setItem(this.ACCESS_TOKEN_KEY, response.accessToken)
    this.localStorageService.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);
  }

  async loadUserProfileOnStartup(): Promise<void> {
    debugger;
    try {
      const token = this.getAccessTokenLocalStorage();
      if (!token) {
        return;
      }

      const userId = Number(this.getUserIdFromToken());
      if (!Number.isFinite(userId) || userId <= 0) {
        return;
      }
      const user = await firstValueFrom(this.userService.getUser(userId));
      this.userSubject.next(user);

    } catch (error: any) {
      this.userSubject.next(null);
      if (error?.status === 401 || error?.status === 403) {
        this.logout();
      }
    }
  }

  updateCurrentUser(patch: Partial<UpdateUserRequest>): void {
    const currentUser = this.userSubject.value;
    if (!currentUser || !patch) return;

    this.userSubject.next({
      ...currentUser,
      firstName: patch.firstName ?? currentUser.firstName,
      lastName: patch.lastName ?? currentUser.lastName,
      phoneNumber: patch.phoneNumber ?? currentUser.phoneNumber,
      birthDate: patch.birthDate ?? currentUser.birthDate,
      profilePictureUrl: patch.profilePictureUrl ?? currentUser.profilePictureUrl,
      address: patch.address
        ? {
          ...currentUser.address,
          ...patch.address
        }
        : currentUser.address
    });
  }

  logout(): void {
    this.localStorageService.removeItem(this.ACCESS_TOKEN_KEY);
    this.localStorageService.removeItem(this.REFRESH_TOKEN_KEY);
    this.userSubject.next(null);
  }
}